<script setup lang="ts">
import { useDrag } from "./composables/drag";
import { reactive, ref, computed, onMounted, watch, PropType } from "vue";
import { CropSelectionModel } from "./models";
import {
  TirPmButton,
  TirPmButtonSizeEnum,
  TirPmButtonVariantEnum,
} from "tir-pm-button";
import { TirPmSlider } from "tir-pm-slider";

const props = defineProps({
  /**
   * Значение
   */
  modelValue: { type: String, default: "" },
  /**
   * Скрыть элементы управления
   */
  hideControls: { type: Boolean, defailt: false },
  /**
   * Отношение сторон кропа
   */
  aspect: { type: Number, default: 1 },
  /**
   * Размер изменения коэффициента зума
   */
  zdk: { type: Number, default: 0.1 },
  /**
   * Фото
   */
  src: { type: String, default: "" },
  /**
   * Мин размер
   */
  minSize: { type: Number, default: 100 },
  /**
   * Отправлять событие с данными о позиции обрезки
   */
  sendCropSelection: { type: Boolean, default: true },
  /** размер выделенной области кноппера по умолчанию будет на всю картинку или нет */
  isMaxDefaultSize: { type: Boolean, default: false },
  /** показывать ли точки растягивания области за середины */
  showCenterPoints: { type: Boolean, default: false },
  /** если нет коэффициента (картинка по дефолту должна быть выделена целиком) */
  isNoAspect: { type: Boolean, default: false },
  /**
   * Минимальная высота
   */
  minHeight: { type: Number, default: 290 },
});
/**
 * Начальная позация при ресайзе
 */
const start_resize_pos = reactive({ l: 0, t: 0 });
/**
 * Начальный размер при ресайзе
 */
const start_resize_size = reactive({ w: 0, h: 0 });
/**
 * Активная точка кропа
 */
const $p = ref<HTMLElement>();
/**
 * Позиция фото
 */
const img_pos = ref({ l: 0, t: 0 });
/**
 * Перетаскивание активной точки
 */
const p_drag = useDrag($p);
/**
 * Слежение за тем началось ли перетаскивание точки
 */
watch(p_drag.isDrag, (): void => {
  if (p_drag.isDrag.value) {
    start_resize_pos.l = crop_pos.value.l;
    start_resize_pos.t = crop_pos.value.t;
    start_resize_size.w = crop_size.w;
    start_resize_size.h = crop_size.h;
  } else {
    start_resize_pos.l = 0;
    start_resize_pos.t = 0;
    start_resize_size.w = 0;
    start_resize_size.h = 0;
    p_drag.setX(0);
    p_drag.setY(0);
    $p.value = undefined;
    setCrop();
  }
});
/**
 * Позация точки
 */
const p_pos = computed((): { l: number; t: number } => {
  return { l: p_drag.x.value, t: p_drag.y.value };
});
/**
 * Слежение за позицией точки
 */
watch(p_pos, () => {
  if (!p_drag.isDrag.value || !$p.value) return;
  let lw = start_resize_size.w,
    lh = start_resize_size.h,
    l = start_resize_pos.l,
    t = start_resize_pos.t;
  if (!props.aspect) {
    if (get_is_drag_p("", "top")) {
      lh = start_resize_size.h - p_drag.y.value;
      t = start_resize_pos.t + p_drag.y.value;
      if (t < crop_min.value.t) t = crop_min.value.t;
      if (t + lh > start_resize_pos.t + start_resize_size.h) {
        t = crop_min.value.t;
        lh = start_resize_pos.t + start_resize_size.h - crop_min.value.t;
      }
    } else if (get_is_drag_p("", "bottom")) {
      lh = start_resize_size.h + p_drag.y.value;
      if (start_resize_pos.t + lh > crop_max.value.t) {
        lh = crop_max.value.t - start_resize_pos.t;
      }
    }
    if (get_is_drag_p("left", "")) {
      lw = start_resize_size.w - p_drag.x.value;
      l = start_resize_pos.l + p_drag.x.value;
      if (l < crop_min.value.l) l = crop_min.value.l;
      if (l + lw > start_resize_pos.l + start_resize_size.w) {
        l = crop_min.value.l;
        lw = start_resize_pos.l + start_resize_size.w - crop_min.value.l;
      }
    } else if (get_is_drag_p("right", "")) {
      lw = start_resize_size.w + p_drag.x.value;
      if (start_resize_pos.l + lw > crop_max.value.l) {
        lw = crop_max.value.l - start_resize_pos.l;
      }
    }
  } else {
    l = start_resize_pos.l;
    t = start_resize_pos.t;
    if (get_is_drag_p("left", "")) {
      lw = start_resize_size.w - p_drag.x.value;
      l = crop_pos.value.l - (lw - crop_size.w);
    } else if (get_is_drag_p("right", "")) {
      lw = start_resize_size.w + p_drag.x.value;
    }
    lh = lw / props.aspect;
    if (get_is_drag_p("center", "top")) {
      lh = start_resize_size.h - p_drag.y.value;
      lw = lh * props.aspect;
    }
    if (get_is_drag_p("center", "bottom")) {
      lh = start_resize_size.h + p_drag.y.value;
      lw = lh * props.aspect;
    }
    if (get_is_drag_p("", "top")) {
      t = crop_pos.value.t - (lh - crop_size.h);
    }
    if (get_is_drag_p("center", "")) {
      l = crop_pos.value.l + (crop_size.w - lw) / 2;
    }
    if (get_is_drag_p("", "middle")) {
      t = crop_pos.value.t + (crop_size.h - lh) / 2;
    }

    if (lh > crop_max.value.t - crop_min.value.t) {
      lh = crop_max.value.t - crop_min.value.t;
      lw = lh * props.aspect;
      l = crop_pos.value.l;
    }
    if (lw > crop_max.value.l - crop_min.value.l) {
      lw = crop_max.value.l - crop_min.value.l;
      lh = lw / props.aspect;
      t = crop_pos.value.t;
    }
    if (l < crop_min.value.l) l = crop_min.value.l;
    if (l + lw > crop_max.value.l) l = crop_max.value.l - lw;
    if (t < crop_min.value.t) t = crop_min.value.t;
    if (t + lh > crop_max.value.t) t = crop_max.value.t - lh;
  }
  if (lw > crop_min_size.value.w) {
    crop_size.w = lw;
    crop_drag.setX(l);
  }
  if (lh > crop_min_size.value.h) {
    crop_size.h = lh;
    crop_drag.setY(t);
  }
});
/**
 * Установка точки
 * $param e
 */
const set_p = (e: MouseEvent | TouchEvent): void => {
  if (ignore.value) return;
  $p.value = e.target as HTMLElement;
  if (!$p.value) return;
  p_drag.start(e);
};
/**
 * Возвращает true если точка с данными классам перетаскиваниется
 * h - класс по горизонтали left, center, right
 * v - класс по вертикали top, middle, bottom
 * $param h
 * $param v
 */
const get_is_drag_p = (
  h: "left" | "center" | "right" | "",
  v: "top" | "middle" | "bottom" | ""
): boolean => {
  return !!(
    (!h || $p.value?.classList.contains(h)) &&
    (!v || $p.value?.classList.contains(v))
  );
};
/**
 * Элемент обертки
 */
const $el = ref<HTMLElement>();
/**
 * Фото
 */
const $img = ref<HTMLImageElement>();
/**
 * Кроп
 */
const $crop = ref<HTMLElement>();
/**
 * 1% от ширины обертки
 */
const w = computed(() => ($el.value?.offsetWidth || 0) / 100);
/**
 * 1% от высоты обертки
 */
const h = computed(() => ($el.value?.offsetHeight || 0) / 100);
/**
 * Возвращает стили позиции
 * $param settings
 */
const get_pos_style = (settings: {
  l: number;
  t: number;
}): { left: string; top: string } => {
  return {
    left: `${settings.l}px`,
    top: `${settings.t}px`,
  };
  // return {
  //   left: `${settings.l / w.value}%`,
  //   top: `${settings.t / h.value}%`,
  // };
};
/**
 * Возвращает стили размера
 * $param settings
 */
const get_size_style = (settings: {
  w: number;
  h: number;
}): { width: string; height: string } => {
  return {
    width: `${settings.w}px`,
    height: `${settings.h}px`,
  };
  // return {
  //   width: `${settings.w / w.value}%`,
  //   height: `${settings.h / h.value}%`,
  // };
};
/**
 * Перетаскивание кропа
 */
const crop_drag = useDrag($crop, p_drag.isDrag);
/**
 * Перетаскивание фото
 */
const img_drag = useDrag($img, p_drag.isDrag);
/**
 * Слежение за тем перетаскивается ли кроп
 */
watch(crop_drag.isDrag, () => {
  if (crop_drag.isDrag.value) return;
  crop_drag.setX(crop_pos.value.l);
  crop_drag.setY(crop_pos.value.t);
  setCrop();
});
/**
 * Слежение за тем перетаскивается ли фото
 */
watch(img_drag.isDrag, () => {
  if (img_drag.isDrag.value) return;
  img_drag.setX(img_pos.value.l);
  img_drag.setY(img_pos.value.t);
  setCrop();
});
watch(img_drag.x, set_img_pos);
watch(img_drag.y, set_img_pos);
/**
 * Размер кропа
 */
const crop_size = reactive({
  w: 0,
  h: 0,
});
/**
 * Размер фото
 */
const img_size = reactive({
  w: 0,
  h: 0,
});
/**
 * Позиция кропа
 */
const crop_pos = computed((): { l: number; t: number } => {
  let x = crop_drag.x.value;
  let y = crop_drag.y.value;
  if (ignore.value)
    return {
      l: x,
      t: y,
    };
  if (x < crop_min.value.l) x = crop_min.value.l;
  if (y < crop_min.value.t) y = crop_min.value.t;
  if (x + crop_size.w > crop_max.value.l) x = crop_max.value.l - crop_size.w;
  if (y + crop_size.h > crop_max.value.t) y = crop_max.value.t - crop_size.h;
  return {
    l: x,
    t: y,
  };
});
/**
 * Установка позиции кропа
 */
function set_img_pos() {
  let x = img_drag.x.value;
  let y = img_drag.y.value;
  if (ignore.value)
    img_pos.value = {
      l: x,
      t: y,
    };
  if (img_size.w < w.value * 100) {
    if (x < img_min.value.l) x = img_min.value.l;
    else if (x + img_size.w > img_max.value.l) x = img_max.value.l - img_size.w;
  } else {
    if (x > img_min.value.l) x = img_min.value.l;
    else if (x + img_size.w < img_max.value.l) x = img_max.value.l - img_size.w;
  }
  if (img_size.h < h.value * 100) {
    if (y < img_min.value.t) y = img_min.value.t;
    else if (y + img_size.h > img_max.value.t) y = img_max.value.t - img_size.h;
  } else {
    if (y > img_min.value.t) y = img_min.value.t;
    else if (y + img_size.h < img_max.value.t) y = img_max.value.t - img_size.h;
  }
  img_pos.value = {
    l: x,
    t: y,
  };
}
/**
 * Минимальная позиция кропа
 */
const crop_min = computed((): { l: number; t: number } => {
  let l = 0;
  let t = 0;
  if (!img_pos.value) return { l, t };
  if (img_pos.value.l > 0) l = img_pos.value.l;
  if (img_pos.value.t > 0) t = img_pos.value.t;
  return { l, t };
});
/**
 * Минимальная позиция фото
 */
const img_min = computed(() => {
  let l = 0;
  let t = 0;
  if (!crop_pos.value) return { l, t };
  if (crop_pos.value.l + crop_size.w - img_size.w > 0)
    l = crop_pos.value.l + crop_size.w - img_size.w;
  if (crop_pos.value.t + crop_size.h - img_size.h > 0)
    t = crop_pos.value.t + crop_size.h - img_size.h;
  return { l, t };
});
/**
 * Максимальная позиция кропа
 */
const crop_max = computed((): { l: number; t: number } => {
  let l = w.value * 100;
  let t = h.value * 100;
  if (!img_pos.value) return { l, t };
  if (img_pos.value.l + img_size.w < w.value * 100)
    l = img_pos.value.l + img_size.w;
  if (img_pos.value.t + img_size.h < h.value * 100)
    t = img_pos.value.t + img_size.h;
  return { l, t };
});
/**
 * Максимальная позиция фото
 */
const img_max = computed((): { l: number; t: number } => {
  let l = w.value * 100;
  let t = h.value * 100;
  if (!crop_pos.value) return { l, t };
  if (crop_pos.value.l + img_size.w < w.value * 100)
    l = crop_pos.value.l + img_size.w;
  if (crop_pos.value.t + img_size.h < h.value * 100)
    t = crop_pos.value.t + img_size.h;
  return { l, t };
});
/**
 * Установка размера кропа по коэффициенту
 * $param k
 */
const set_crop_size = (k: number) => {
  if (!$img.value) return;
  let lh = img_size.h / 2 || h.value * 100;
  let lw = img_size.w / 2 || w.value * 100;
  if (!props.aspect && props.isMaxDefaultSize && props.isNoAspect) {
    crop_size.w = img_size.w;
    crop_size.h = img_size.h;
    return crop_size;
  }

  if ($img.value.naturalHeight < $img.value.naturalWidth) {
    if (lh * k < crop_min_size.value.h) crop_size.h = crop_min_size.value.h;
    else if (lh * k > img_size.h) crop_size.h = img_size.h;
    else crop_size.h = lh * k;
    crop_size.w = crop_size.h * (props.aspect || 1);
  } else {
    if (lw * k < crop_min_size.value.w) crop_size.w = crop_min_size.value.w;
    else if (lw * k > img_size.w) crop_size.w = img_size.w;
    else crop_size.w = lw * k;
    crop_size.h = crop_size.w / (props.aspect || 1);
  }
  // если размер области получился больше исходной картинки
  if (crop_size.w > img_size.w) {
    let k = img_size.w / crop_size.w;
    crop_size.w *= k;
    crop_size.h *= k;
  }
  if (crop_size.h > img_size.h) {
    let k = img_size.h / crop_size.h;
    crop_size.w *= k;
    crop_size.h *= k;
  }
  return crop_size;
};
/**
 * Минимальный размер кропа
 */
const crop_min_size = computed(() => {
  let w = props.minSize;
  let h = props.minSize;
  if (img_size.w < w) w = img_size.w;
  if (img_size.h < h) h = img_size.h;
  if (!props.aspect || !$img.value) return { w, h };
  if ($img.value.naturalHeight > $img.value.naturalWidth) h = w / props.aspect;
  else w = h * props.aspect;
  return { w, h };
});
/**
 * Установка размера фото по коэффициенту
 * $param k
 */
const set_img_size = (k: number) => {
  if (!$img.value || !$el.value) return img_size;
  console.log($el.value.offsetWidth);
  console.log($img.value.naturalWidth);
  const el_aspect = $el.value.offsetWidth / $el.value.offsetHeight;
  const aspect = $img.value.naturalWidth / $img.value.naturalHeight;
  if (!aspect) return img_size;
  // (w.value * (100 * k)) / aspect > $el.value.offsetHeight
  if (aspect < 1 || el_aspect > aspect) {
    img_size.h = h.value * (100 * k);
    img_size.w = img_size.h * aspect;
  } else {
    img_size.w = w.value * (100 * k);
    img_size.h = img_size.w / aspect;
  }
  return img_size;
};
/**
 * Коэффициент размера фото
 */
const img_size_k = ref(1);
/**
 * Старое значение размера обрезаемого изображения
 */
const oldZoomValue = ref(0);
/**
 * Изменения значения размера обрезаемого изображения
 */
const changeMaxScale = (maxZoom: {
  value: number & string;
  percent: number & string;
}) => {
  if (oldZoomValue.value < maxZoom.value) {
    zoom("+");
  } else if (oldZoomValue.value > maxZoom.value) {
    zoom("-");
  }
  oldZoomValue.value = maxZoom.value;
  setCrop();
};

/**
 * При нажатии на "сохранить"
 */
const onClickAccept = () => {
  setCrop();
  emit("save");
};

/**
 * Функция зума фото
 * $param z
 */
const zoom = (z: "+" | "-") => {
  ignore.value = true;
  const l = crop_pos.value.l + crop_size.w / 2 - img_pos.value.l;
  const t = crop_pos.value.t + crop_size.h / 2 - img_pos.value.t;
  const kl = l / img_size.w;
  const kt = t / img_size.h;
  const old_size = Object.assign({}, img_size);
  const old_pos = Object.assign({}, img_pos.value);
  if (z == "+") img_size_k.value += props.zdk;
  else img_size_k.value -= props.zdk;
  if (img_size_k.value < 1) {
    img_size_k.value = 1;
  }
  set_img_size(img_size_k.value);
  const dx = Math.abs(img_size.w - old_size.w) * kl;
  const dy = Math.abs(img_size.h - old_size.h) * kt;
  let x = old_pos.l,
    y = old_pos.t;
  if (z == "+") {
    x -= dx;
    y -= dy;
  } else {
    x += dx;
    y += dy;
  }
  img_drag.setX(x);
  img_drag.setY(y);
  setTimeout(() => (ignore.value = false));
  setCrop();
};
/**
 * Позация по умолчания
 * $param size
 */
const get_default_pos = (size: {
  w: number;
  h: number;
}): { l: number; t: number } => {
  let l = w.value * 50;
  let t = h.value * 50;
  if (size.w) l -= size.w / 2;
  if (size.h) t -= size.h / 2;
  return { l, t };
};
/**
 * Игнорировать ограничения позиции фото
 */
const ignore = ref(false);
/**
 * Установка позиций и размеров по умолчанию для фото и кропа
 */
const set_default = () => {
  img_on_load(props.src, () => {
    ignore.value = true;
    set_img_size(img_size_k.value);
    set_crop_size(props.isMaxDefaultSize ? 2 : 0.5);
    const local_img_pos = get_default_pos(img_size);
    img_drag.setX(local_img_pos.l);
    img_drag.setY(local_img_pos.t);
    const local_crop_pos = get_default_pos(crop_size);
    crop_drag.setX(local_crop_pos.l);
    crop_drag.setY(local_crop_pos.t);
    if (props.src) {
      setTimeout(() => (ignore.value = false));
      setTimeout(() => setCrop(true));
    }
  });
};
/**
 * * Выполнение cb после загрузки изображения
 * @param _src Ссылка на изображение
 * @param _cb Колбэк
 */
const img_on_load = (_src: string, _cb: Function) => {
  const _img = new Image();
  _img.onload = () => _cb();
  _img.src = _src;
};
/**
 * Фото для кропа
 */
const src = computed((): string => props.src);
/**
 * Слежение за изменение фото для кропа
 */
watch(src, () => setTimeout(set_default));
/**
 * При загрузке dom
 */
onMounted(() => setTimeout(set_default));
/**
 * События
 */
const emit = defineEmits([
  "update:modelValue",
  "crop",
  "save",
  "choseOtherPhoto",
  "onCroppedPhoto",
]);

/**
 * Выбрать другое фото
 */
const choseOtherPhoto = () => {
  emit("choseOtherPhoto");
};
/**
 * Установить обрезанное изображение
 */
const setCrop = (isSetDefault: boolean = false) => {
  if (!$img.value) return;
  if (props.sendCropSelection) {
    const _image = new Image();
    _image.onload = () => {
      const scaleX = _image.width / img_size.w;
      const scaleY = _image.height / img_size.h;
      emit(
        "crop",
        new CropSelectionModel(
          (crop_pos.value.l - img_pos.value.l) * scaleX,
          (crop_pos.value.t - img_pos.value.t) * scaleY,
          crop_size.w * scaleX,
          crop_size.h * scaleY
        )
      );
    };
    _image.src = props.src;
  }

  const croppedImage = crop(
    $img.value,
    crop_pos.value.l - img_pos.value.l,
    crop_pos.value.t - img_pos.value.t,
    crop_size.w,
    crop_size.h
  );
  emit("onCroppedPhoto", croppedImage, isSetDefault);
};
/**
 * Извлечение MIME-типа из base64 строки
 * @param base64
 * @returns {string}
 */
const getMimeTypeFromBase64 = (base64: string): string => {
  try {
    const match = base64.match(/^data:(.*?);base64,/);
    return match ? match[1] : "image/png"; // по умолчанию "image/png" если не найден MIME-тип
  } catch (e) {
    console.error(e);
    return "";
  }
};

/**
 * Проверка, является ли строка URL
 * @param str
 * @returns {boolean}
 */
const isUrl = (str: string): boolean => {
  const urlPattern = /^(https?:\/\/)/;
  return urlPattern.test(str);
};

/**
 * Обрезать изображение
 */
const crop = (
  image: HTMLImageElement,
  dx: number,
  dy: number,
  dw: number,
  dh: number
): string => {
  try {
    if (props.src && isUrl(props.src)) {
      return props.src;
    } else {
      const cnvs = document.createElement("canvas");
      const wk = image.naturalWidth / image.width;
      const hk = image.naturalHeight / image.height;
      cnvs.width = dw * wk;
      cnvs.height = dh * hk;
      if (!cnvs) return "";

      const cntx = cnvs.getContext("2d");
      if (!cntx) return "";

      cntx.drawImage(
        image,
        dx * wk,
        dy * hk,
        dw * wk,
        dh * hk,
        0,
        0,
        dw * wk,
        dh * hk
      );

      const mime = getMimeTypeFromBase64(props.src) ?? "image/png";
      return cnvs.toDataURL(mime);
    }
  } catch (e) {
    console.error(`Ошибка обрезки\n\n${e}`);
  }
};
</script>

<template>
  <div class="tir-pm-cropper-container">
    <div
      class="tir-pm-cropper-container__wrapper"
      :style="{
        height: props.minHeight + 'px',
      }"
    >
      <div class="tir-pm-cropper" ref="$el">
        <!-- Фото -->
        <img
          :class="{ 'is-drag': img_drag.isDrag.value }"
          ref="$img"
          alt="SRC Image"
          :style="[get_size_style(img_size), get_pos_style(img_pos)]"
          :src="src"
        />
        <!-- Кроп -->
        <div
          :class="{ 'is-drag': crop_drag.isDrag.value || p_drag.isDrag.value }"
          class="crop"
          ref="$crop"
          :style="[
            get_size_style(crop_size),
            get_pos_style(crop_pos),
            {
              minWidth: `${crop_min_size.w}px`,
              minHeight: `${crop_min_size.h}px`,
            },
          ]"
        >
          <!-- Точки кропа -->
          <i
            :class="{ 'is-drag': get_is_drag_p('left', 'top') }"
            class="left top"
            ref="$lt"
            @mousedown="set_p"
            @touchstart.prevent="set_p"
          />
          <i
            :class="{ 'is-drag': get_is_drag_p('center', 'top') }"
            class="center top"
            @mousedown="set_p"
            @touchstart.prevent="set_p"
            v-if="showCenterPoints"
          />
          <i
            :class="{ 'is-drag': get_is_drag_p('right', 'top') }"
            class="right top"
            @mousedown="set_p"
            @touchstart.prevent="set_p"
          />
          <i
            :class="{ 'is-drag': get_is_drag_p('left', 'middle') }"
            class="left middle"
            @mousedown="set_p"
            @touchstart.prevent="set_p"
            v-if="showCenterPoints"
          />
          <i
            :class="{ 'is-drag': get_is_drag_p('right', 'middle') }"
            class="right middle"
            @mousedown="set_p"
            @touchstart.prevent="set_p"
            v-if="showCenterPoints"
          />
          <i
            :class="{ 'is-drag': get_is_drag_p('left', 'bottom') }"
            class="left bottom"
            @mousedown="set_p"
            @touchstart.prevent="set_p"
          />
          <i
            :class="{ 'is-drag': get_is_drag_p('center', 'bottom') }"
            class="center bottom"
            @mousedown="set_p"
            @touchstart.prevent="set_p"
            v-if="showCenterPoints"
          />
          <i
            :class="{ 'is-drag': get_is_drag_p('right', 'bottom') }"
            class="right bottom"
            @mousedown="set_p"
            @touchstart.prevent="set_p"
          />
          <!-- Крестик в центре кропа -->
          <span></span>
        </div>
      </div>
    </div>
    <div v-if="!hideControls" class="tir-pm-cropper-container__controls-size">
      <TirPmSlider
        :step="10"
        :minValue="0"
        :maxValue="0"
        @maxValue="changeMaxScale"
        class="tir-pm-cropper-container__controls-size-input"
      ></TirPmSlider>
    </div>

    <div class="tir-pm-cropper-container__controls" v-if="!hideControls">
      <TirPmButton
        :size="TirPmButtonSizeEnum.Small"
        @click="onClickAccept"
        :variant="TirPmButtonVariantEnum.PrimaryState"
        >Сохранить</TirPmButton
      >
      <TirPmButton
        @click="choseOtherPhoto"
        :size="TirPmButtonSizeEnum.Small"
        :variant="TirPmButtonVariantEnum.LinkState"
        >Загрузить другое фото</TirPmButton
      >
    </div>
  </div>
</template>

<style lang="scss">
// Все перменные стилей в папке assets/style/n-components/cropper.scss
.tir-pm-cropper-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
  &__controls {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }
  &__wrapper {
    width: 100%;
  }
  &__controls-size {
    display: flex;
    justify-content: center;
  }
  &__controls-size-input {
    max-width: 400px;
    width: 100%;
  }
}
.tir-pm-cropper {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background-image: url(./assets/image/bg.png);
  user-select: none;
  width: 100%;
  height: 100%;

  // Зум
  .zoom {
    display: inline-flex;
    flex-direction: column;
    align-items: flex-end;
    position: absolute;
    right: 0;
    top: 0;
    z-index: 101;
  }

  // Фото
  img {
    position: absolute;
    display: inline-flex;
  }

  // Кроп
  .crop {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    border-style: var(--tir-cropper-border-style);
    border-width: var(--tir-cropper-border-width);
    border-color: var(--tir-cropper-border);
    transition-duration: var(--tir-cropper-transition-duration);
    transition-timing-function: var(--tir-cropper-transition-timing-function);
    transition-property: border;
    box-shadow: 0 0 0 10000px rgba(0, 0, 0, var(--tir-cropper-opacity));
    z-index: 100;

    &.is-drag {
      border-color: var(--tir-cropper-active-border);
      border-style: var(--tir-cropper-active-border-style);
    }

    // крестик
    > span {
      display: flex;
      align-items: center;
      justify-content: center;

      &::before,
      &::after {
        content: "";
        position: absolute;
        background-color: var(--tir-cropper-border);
      }

      &::before {
        width: var(--tir-cropper-border-width);
        height: var(--tir-cropper-point-size);
      }

      &::after {
        height: var(--tir-cropper-border-width);
        width: var(--tir-cropper-point-size);
      }
    }

    // Точка
    > i {
      z-index: 100;
      position: absolute;
      display: inline-block;
      width: var(--tir-cropper-point-size);
      height: var(--tir-cropper-point-size);
      transition-duration: var(--tir-cropper-transition-duration);
      transition-timing-function: var(--tir-cropper-transition-timing-function);

      &::before,
      &::after {
        content: "";
        position: absolute;
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        border-radius: var(--tir-cropper-point-radius);
        transition-duration: inherit;
        transition-timing-function: inherit;
      }

      &::before {
        background: var(--tir-cropper-point-background);
        opacity: var(--tir-cropper-point-opacity);
      }

      &::after {
        border-style: var(--tir-cropper-point-border-style);
        border-width: var(--tir-cropper-point-border-width);
        border-color: var(--tir-cropper-point-border);
      }

      $p: calc(var(--tir-cropper-point-size) / -2);
      $p_active: calc(var(--tir-cropper-point-active-size) / -2);

      &.is-drag {
        width: var(--tir-cropper-point-active-size);
        height: var(--tir-cropper-point-active-size);

        &::before,
        &::after {
          border-radius: var(--tir-cropper-point-active-radius);
        }

        &::before {
          background: var(--tir-cropper-point-active-background);
          opacity: var(--tir-cropper-point-active-opacity);
        }

        &::after {
          border-style: var(--tir-cropper-point-active-border-style);
          border-color: var(--tir-cropper-point-active-border);
        }
      }

      &.left {
        left: $p;

        &.is-drag {
          left: $p_active;
        }

        &.top {
          cursor: nwse-resize;
        }

        &.bottom {
          cursor: nesw-resize;
        }
      }

      &.center {
        cursor: ns-resize;
        left: calc(50% - var(--tir-cropper-point-size) / 2);

        &.is-drag {
          left: calc(50% - var(--tir-cropper-point-active-size) / 2);
        }
      }

      &.right {
        right: $p;

        &.is-drag {
          right: $p_active;
        }

        &.top {
          cursor: nesw-resize;
        }

        &.bottom {
          cursor: nwse-resize;
        }
      }

      &.top {
        top: $p;

        &.is-drag {
          top: $p_active;
        }
      }

      &.middle {
        cursor: ew-resize;
        top: calc(50% - var(--tir-cropper-point-size) / 2);

        &.is-drag {
          top: calc(50% - var(--tir-cropper-point-active-size) / 2);
        }
      }

      &.bottom {
        bottom: $p;

        &.is-drag {
          bottom: $p_active;
        }
      }
    }
  }

  img,
  .crop {
    cursor: grab;

    &.is-drag {
      cursor: grabbing;
    }
  }
}
</style>
